import Cookies from "js-cookie";
import { type IntervalType } from "../BookingsChart/constants";
import {
  getBucketCountForInterval,
  getDateRangeForInterval,
  calculateNextDate,
  calculatePreviousDate,
} from "../BookingsChart/helpers";

const CHART_COOKIE_NAME = "bookings-chart:state";
const CHART_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days

export type ChartVisibilityState = "collapsed" | "expanded";

export const getIsChartDefaultExpanded = (): boolean => {
  const cookieValue = Cookies.get(CHART_COOKIE_NAME);
  const collapsed: ChartVisibilityState = "collapsed";
  return cookieValue !== collapsed;
};

export interface ChartState {
  referenceDate: Date;
  intervalType: IntervalType;
  visibility: ChartVisibilityState;
  dateRange: { start: Date; end: Date };
}

export type ChartAction =
  | { type: "SET_INTERVAL"; intervalType: IntervalType }
  | { type: "NAVIGATE_NEXT" }
  | { type: "NAVIGATE_PREVIOUS" }
  | { type: "RESET_TO_TODAY" }
  | { type: "TOGGLE_CHART_VISIBILITY" };

const recalculateDateRange = (currentState: ChartState, updates: Partial<ChartState>): ChartState => {
  const newState = { ...currentState, ...updates };
  return {
    ...newState,
    dateRange: getDateRangeForInterval({
      referenceDate: newState.referenceDate,
      intervalType: newState.intervalType,
      bucketCount: getBucketCountForInterval(newState.intervalType),
    }),
  };
};

export const chartReducer = (state: ChartState, action: ChartAction): ChartState => {
  switch (action.type) {
    case "SET_INTERVAL":
      return recalculateDateRange(state, {
        intervalType: action.intervalType,
        referenceDate: new Date(), // Reset to today when changing intervals
      });

    case "NAVIGATE_NEXT":
      return recalculateDateRange(state, {
        referenceDate: calculateNextDate(state.referenceDate, state.intervalType),
      });

    case "NAVIGATE_PREVIOUS":
      return recalculateDateRange(state, {
        referenceDate: calculatePreviousDate(state.referenceDate, state.intervalType),
      });

    case "RESET_TO_TODAY":
      return recalculateDateRange(state, { referenceDate: new Date() });

    case "TOGGLE_CHART_VISIBILITY": {
      const newVisibility: ChartVisibilityState = state.visibility === "expanded" ? "collapsed" : "expanded";
      document.cookie = `${CHART_COOKIE_NAME}=${newVisibility}; path=/; max-age=${CHART_COOKIE_MAX_AGE}`;
      return {
        ...state,
        visibility: newVisibility,
      };
    }

    default:
      const _exhaustiveCheck: never = action;
      return _exhaustiveCheck;
  }
};
